import i18next from 'i18next';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { Message, TestFunction } from 'yup';
import {
  ExtraFieldFormInfoFragment,
  FieldType,
} from '../../__generated__/graphql';
import { ExtraFieldsVisibility } from './extraFieldsVisibility';
import { getExtraFieldInputName, recordTypeFormKey } from './logic';
import {
  ExtraFieldContextParam,
  ExtraFieldsFormId,
  ExtraFieldsSupportedObject,
} from './types';
import { useObjectExtraFields } from './useObjectExtraFields';

type Props = {
  objectApiName: ExtraFieldsSupportedObject;
  formId: ExtraFieldsFormId;
  contextParams: ExtraFieldContextParam[];
};

export const useExtraFieldsFormValidationByFormId7 = ({
  objectApiName,
  contextParams,
  formId,
}: Props) => {
  const { getFormFields, loading } = useObjectExtraFields(objectApiName);

  const extraFieldFormInfos = getFormFields(formId);
  const { extraFieldsValidationSchema, applyExtraFieldsValidationSchema } =
    useExtraFieldsFormValidation7({
      extraFieldFormInfos,
      contextParams,
    });
  return {
    extraFieldsValidationSchema,
    applyExtraFieldsValidationSchema,
    loading,
  };
};

export const useExtraFieldsFormValidation7 = ({
  extraFieldFormInfos,
  contextParams,
}: {
  extraFieldFormInfos: readonly ExtraFieldFormInfoFragment[];
  contextParams: ExtraFieldContextParam[];
}) => {
  const extraFieldsValidationSchema = useMemo(() => {
    const requiredExtraFields = extraFieldFormInfos.filter(
      (field) => field.required && field.fieldInfo.type !== FieldType.Boolean,
    );
    if (!requiredExtraFields.length) {
      return null;
    }
    const visibility = new ExtraFieldsVisibility();
    return requiredExtraFields.map((field) => [
      getExtraFieldInputName(field.fieldApiName, field.fieldInfo.type),
      getYupValidation(visibility, field, contextParams),
    ]);
  }, [extraFieldFormInfos, contextParams]);

  const applyExtraFieldsValidationSchema = useCallback(
    (schema: yup.AnyObjectSchema) => {
      if (!extraFieldsValidationSchema?.length) {
        return schema;
      }
      return schema.shape(Object.fromEntries(extraFieldsValidationSchema));
    },
    [extraFieldsValidationSchema],
  );

  return {
    extraFieldsValidationSchema,
    applyExtraFieldsValidationSchema,
  };
};

function getYupValidation(
  visibility: ExtraFieldsVisibility,
  field: ExtraFieldFormInfoFragment,
  contextParams: ExtraFieldContextParam[],
) {
  function requiredIfVisible(this: yup.TestContext, value: unknown) {
    const formData = this.parent;
    const isVisible = visibility.isFieldVisible(field, {
      recordTypeName: formData[recordTypeFormKey],
      contextParams,
    });
    if (!isVisible) {
      return true;
    }
    return value !== null && value !== undefined && value !== '';
  }

  function addRequiredIfVisible<T>(schema: {
    test(name: string, message: Message, test: TestFunction): T;
  }) {
    return schema.test(
      'requiredIfVisible',
      i18next.t('validations.mixed.required'),
      requiredIfVisible,
    );
  }

  const fieldType = field.fieldInfo.type;
  if (fieldType === FieldType.Boolean) {
    return addRequiredIfVisible(yup.boolean());
  }
  if (
    fieldType === FieldType.Integer ||
    fieldType === FieldType.Currency ||
    fieldType === FieldType.Double
  ) {
    return addRequiredIfVisible(
      yup
        .mixed()
        .transform((value) => {
          if (value === '' || value === undefined) {
            return null;
          }
          const numberValue = Number(value);
          return isNaN(numberValue) ? null : numberValue;
        })
        .nullable()
        .test('is-number', i18next.t('validations.mixed.default'), (value) => {
          return value === null || typeof value === 'number';
        }),
    );
  }
  if (fieldType === FieldType.Date || fieldType === FieldType.Datetime) {
    return addRequiredIfVisible(
      yup
        .mixed()
        .transform((value) => {
          if (value === '' || value === undefined) {
            return null;
          }
          const dateValue = value instanceof Date ? value : new Date(value);
          return isNaN(dateValue.getTime()) ? null : dateValue;
        })
        .nullable()
        .test('is-date', i18next.t('validations.mixed.default'), (value) => {
          return value === null || value instanceof Date;
        }),
    );
  }
  return addRequiredIfVisible(yup.string());
}
